import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import classes from "./navFlyout.module.scss";
import Input from "../components/Input";
import NewBugReportsChip from "../components/NewBugReportsChip";
import { NavCategory, NavItem } from "./navMenu";

interface NavFlyoutProps {
    /** The permission-filtered category to list. */
    category: NavCategory;
    activeTo?: string;
    onNavigate: (event: React.MouseEvent, to: string) => void;
    onClose: () => void;
}

interface Section {
    title: string;
    items: NavItem[];
}

/**
 * Side panel beside the sidebar. Lookups and System list their pages in
 * labelled columns with a filter; in the collapsed icon rail, a module group
 * opens here as a single list.
 */
const NavFlyout: React.FC<NavFlyoutProps> = ({ category, activeTo, onNavigate, onClose }) => {
    const [filter, setFilter] = useState("");
    const isWide = category.kind === "flyout";

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    const sections: Section[] = useMemo(() => {
        const query = filter.trim().toLowerCase();
        const visible = category.items.filter((item) => !query || item.label.toLowerCase().includes(query));
        if (!isWide) {
            return [{ title: category.category, items: visible }];
        }
        const bySubgroup = new Map<string, NavItem[]>();
        visible.forEach((item) => {
            const key = item.subgroup || category.category;
            bySubgroup.set(key, [...(bySubgroup.get(key) || []), item]);
        });
        return Array.from(bySubgroup.entries()).map(([title, items]) => ({ title, items }));
    }, [category, filter, isWide]);

    const subtitle = isWide
        ? `${category.items.length} ${category.key === "lookups" ? "reference lists, grouped by what they describe" : "admin and monitoring pages"}`
        : "";

    return (
        <>
            <div className={classes.scrim} onClick={onClose} aria-hidden="true" />
            <section className={`${classes.panel} ${isWide ? classes.panelWide : ""}`} aria-label={category.category}>
                <div className={classes.head}>
                    <div className={classes.headText}>
                        <span className={classes.title}>{category.category}</span>
                        {subtitle && <span className={classes.subtitle}>{subtitle}</span>}
                    </div>
                    <button
                        type="button"
                        className={`btn-unstyled ${classes.close}`}
                        onClick={onClose}
                        aria-label={`Close ${category.category}`}
                        title="Close (Esc)"
                    >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                    </button>
                </div>

                {isWide && (
                    <div className={classes.filter}>
                        <Input
                            type="text"
                            label={`Filter ${category.category.toLowerCase()}`}
                            id={`nav-filter-${category.key}`}
                            value={filter}
                            onChange={(_name, value) => setFilter(value as string)}
                            icon="bi bi-search"
                        />
                    </div>
                )}

                <div className={`${classes.body} ${isWide ? classes.bodyColumns : ""}`}>
                    {sections.map((section) => (
                        <div key={section.title} className={classes.section}>
                            {isWide && <span className={classes.sectionTitle}>{section.title}</span>}
                            {section.items.map((item) => {
                                const isActive = item.to === activeTo;
                                return (
                                    <Link
                                        key={item.to}
                                        to={item.to}
                                        className={`${classes.item} ${isActive ? classes.itemActive : ""}`}
                                        aria-current={isActive ? "page" : undefined}
                                        onClick={(event) => onNavigate(event, item.to)}
                                    >
                                        <span className={classes.itemLabel}>{item.shortLabel ?? item.label}</span>
                                        {item.to === "/system/bug-reports" && <NewBugReportsChip />}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                    {sections.length === 0 && (
                        <span className={classes.empty}>Nothing matches “{filter}”.</span>
                    )}
                </div>
            </section>
        </>
    );
};

export default NavFlyout;
